import cx from "classnames";
import { forwardRef } from "react";

import { useSidebar } from "./useSidebar";

import {
  RESIZE_HANDLE_GROUP_CLASS,
  ResizeHandleLine,
} from "../ResizeHandleLine";

export const SidebarRail = forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button">
>(({ className, ...props }, ref) => {
  const { toggleSidebar } = useSidebar();

  return (
    <button
      ref={ref}
      data-sidebar="rail"
      aria-label="Toggle Sidebar"
      tabIndex={-1}
      onClick={toggleSidebar}
      title="Toggle Sidebar"
      className={cx(
        // Named resize group so the shared line reacts to THIS rail's hover
        // only (not the sidebar root `group`).
        RESIZE_HANDLE_GROUP_CLASS,
        "absolute inset-y-0 z-[2] hidden w-4 -translate-x-1/2 bg-transparent transition-all ease-out group-data-[side=left]:-right-4 group-data-[side=right]:-left-0.5 sm:flex",
        "[[data-side=left]_&]:cursor-w-resize [[data-side=right]_&]:cursor-e-resize",
        "[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize",
        "group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:hover:bg-puntt-neutral-gray-3",
        "[[data-side=left][data-collapsible=offcanvas]_&]:-right-2",
        "[[data-side=right][data-collapsible=offcanvas]_&]:-left-2",
        className,
      )}
      {...props}
    >
      {/*
        The one standard draggable line (same component the Ask Puntt panes use).
        showAtRest=false: the sidebar container's own `border-r` already draws the
        1px rest hairline, so this overlay stays invisible until hover/drag and
        never doubles the line. Hovering the header collapse toggle lights the
        SAME line via the sidebar root `group-has-[…:hover]` so users associate
        that button with this divider.
      */}
      <ResizeHandleLine
        showAtRest={false}
        testId="sidebar-rail-line"
        className={cx(
          "group-has-[[data-testid=sidebar-collapse-trigger]:hover]:w-0.5",
          "group-has-[[data-testid=sidebar-collapse-trigger]:hover]:bg-puntt-neutral-gray-8",
        )}
      />
    </button>
  );
});

SidebarRail.displayName = "SidebarRail";
